import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { CompanyOfficeLocation } from '@/api/v4/organization.api';
import { deleteCompanyOfficeLocation } from '@/api/v4/organization.api';
import { QueryKey } from '@/config/query-client';
import { notify } from '@/ui/snackbar/notify';

import { companyIdState } from '../../company.state';
import { useCompanyFetchMode } from '../../company-fetch-context';
import { showOfficeLocationsState } from '../office-locations.state';
import { useAtomValue, useSetAtom } from 'jotai';

export const useDeleteOfficeLocation = () => {
  const companyId = useAtomValue(companyIdState);
  const fetchMode = useCompanyFetchMode();
  const { t } = useTranslation('default');
  const queryClient = useQueryClient();

  const setOfficeLocationsVisible = useSetAtom(showOfficeLocationsState);

  const deleteOfficeLocationMutation = useMutation({
    mutationFn: async (officeLocationId: string) => {
      if (!companyId) {
        return;
      }
      return deleteCompanyOfficeLocation(companyId, officeLocationId);
    },
    onError: () => {
      notify({ message: t`default:unknownError` });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [QueryKey.CompanyOfficeLocations, fetchMode, companyId],
      });

      const officeLocations = queryClient.getQueryData<CompanyOfficeLocation[]>(
        [QueryKey.CompanyOfficeLocations, fetchMode, companyId],
      );

      const isLastLocation = officeLocations?.length === 1;

      if (isLastLocation) {
        setOfficeLocationsVisible(false);
      }
    },
  });
  return {
    deleteOfficeLocation: deleteOfficeLocationMutation.mutateAsync,
  };
};
